import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Languages, Loader2, Sparkles, Wand2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { ApiRequestError, apiGet, apiPatch, apiPost } from '@/lib/api';

export function useAiStatus() {
  return useQuery({
    queryKey: ['ai', 'status'],
    queryFn: () => apiGet<{ enabled: boolean; limit: number; used: number; remaining: number }>('/ai/status'),
    staleTime: 60_000,
  });
}

const failed = (error: unknown) => toast.error(error instanceof ApiRequestError ? error.message : 'The AI assistant could not answer');

type Rewrite = 'improve' | 'shorter' | 'friendlier' | 'formal' | 'fix' | 'translate_ar' | 'translate_en';

/** Improve, shorten, re-tone or translate a piece of text in place. */
export function AiRewriteMenu({ text, onResult, size = 'sm' }: { text: string; onResult: (text: string) => void; size?: 'sm' | 'icon' }) {
  const { data: status } = useAiStatus();
  const rewrite = useMutation({
    mutationFn: (action: Rewrite) => apiPost<{ text: string }>('/ai/rewrite', { text, action }),
    onSuccess: (result) => onResult(result.text),
    onError: failed,
  });
  if (!status?.enabled) return null;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button type="button" variant="ghost" size="sm" disabled={!text.trim() || rewrite.isPending} title="AI writing help" className={size === 'icon' ? 'h-8 w-8 p-0' : ''}>
          {rewrite.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wand2 className="h-4 w-4" />}
          {size === 'sm' && 'AI'}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {(
          [
            ['improve', 'Improve writing'],
            ['fix', 'Fix spelling & grammar'],
            ['shorter', 'Make shorter'],
            ['friendlier', 'Make friendlier'],
            ['formal', 'Make more formal'],
          ] as const
        ).map(([action, label]) => (
          <DropdownMenuItem key={action} onClick={() => rewrite.mutate(action)}>
            {label}
          </DropdownMenuItem>
        ))}
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={() => rewrite.mutate('translate_ar')}>
          <Languages className="me-2 h-4 w-4" /> Translate to Arabic
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => rewrite.mutate('translate_en')}>
          <Languages className="me-2 h-4 w-4" /> Translate to English
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/** Suggest a reply and summarise, for a conversation in the inbox. */
export function AiConversationTools({ source, id, onReply, leadId }: { source: 'inbox' | 'whatsapp'; id: string; onReply: (text: string) => void; leadId?: string | null }) {
  const { data: status } = useAiStatus();
  const [summary, setSummary] = useState<{ summary: string; needs: string[]; nextStep: string; sentiment: string } | null>(null);
  const reply = useMutation({
    mutationFn: () => apiPost<{ reply: string }>('/ai/conversation/reply', { source, id }),
    onSuccess: (result) => onReply(result.reply),
    onError: failed,
  });
  const summarise = useMutation({
    mutationFn: () => apiPost<{ summary: string; needs: string[]; nextStep: string; sentiment: string }>('/ai/conversation/summary', { source, id }),
    onSuccess: setSummary,
    onError: failed,
  });
  const [fields, setFields] = useState<Record<string, string | number> | null>(null);
  const extract = useMutation({
    mutationFn: () => apiPost<Record<string, string | number>>('/ai/extract', { source, id }),
    onSuccess: (result) => (Object.keys(result).length ? setFields(result) : toast.info('Nothing to fill in yet — the customer has not shared details.')),
    onError: failed,
  });
  const saveLead = useMutation({
    mutationFn: () => apiPatch(`/leads/${leadId}`, Object.fromEntries(Object.entries(fields ?? {}).map(([key, value]) => [key, key === 'budget' ? String(value) : value]))),
    onSuccess: () => {
      toast.success('Lead updated from the chat');
      setFields(null);
    },
    onError: failed,
  });
  if (!status?.enabled) return null;
  return (
    <>
      {leadId && (
        <button type="button" onClick={() => extract.mutate()} disabled={extract.isPending} className="flex items-center gap-1 rounded-md px-2.5 py-1 text-muted-foreground hover:bg-accent">
          {extract.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : <Wand2 className="h-3 w-3" />} Fill lead
        </button>
      )}
      {fields && (
        <div className="absolute bottom-full start-3 end-3 z-10 mb-2 rounded-xl border bg-popover p-3 text-sm shadow-xl">
          <div className="mb-2 flex items-center justify-between">
            <span className="flex items-center gap-1 font-semibold">
              <Sparkles className="h-3.5 w-3.5 text-primary" /> From the chat
            </span>
            <button className="text-xs text-muted-foreground hover:underline" onClick={() => setFields(null)}>
              Close
            </button>
          </div>
          <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1">
            {Object.entries(fields).map(([key, value]) => (
              <div key={key} className="contents">
                <dt className="text-xs text-muted-foreground">{key.replace(/([A-Z])/g, ' $1').toLowerCase()}</dt>
                <dd className="truncate">{String(value)}</dd>
              </div>
            ))}
          </dl>
          <Button size="sm" className="mt-3 w-full" onClick={() => saveLead.mutate()} disabled={saveLead.isPending}>
            Save to the lead
          </Button>
        </div>
      )}
      <button type="button" onClick={() => reply.mutate()} disabled={reply.isPending} className="flex items-center gap-1 rounded-md px-2.5 py-1 text-primary hover:bg-primary/10">
        {reply.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : <Sparkles className="h-3 w-3" />} Suggest reply
      </button>
      <button type="button" onClick={() => summarise.mutate()} disabled={summarise.isPending} className="flex items-center gap-1 rounded-md px-2.5 py-1 text-muted-foreground hover:bg-accent">
        {summarise.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : null} Summarise
      </button>
      {summary && (
        <div className="absolute bottom-full start-3 end-3 mb-2 rounded-xl border bg-popover p-3 text-sm shadow-xl">
          <div className="mb-1 flex items-center justify-between">
            <span className="flex items-center gap-1 font-semibold">
              <Sparkles className="h-3.5 w-3.5 text-primary" /> Summary
            </span>
            <button className="text-xs text-muted-foreground hover:underline" onClick={() => setSummary(null)}>
              Close
            </button>
          </div>
          <p>{summary.summary}</p>
          {summary.needs.length > 0 && <p className="mt-1 text-xs text-muted-foreground">Wants: {summary.needs.join(' · ')}</p>}
          {summary.nextStep && <p className="mt-1 text-xs font-medium">Next: {summary.nextStep}</p>}
        </div>
      )}
    </>
  );
}

/** A briefing on a lead, contact, company or deal before calling. */
export function AiRecordSummary({ entityType, entityId }: { entityType: string; entityId: string }) {
  const { data: status } = useAiStatus();
  const brief = useMutation({
    mutationFn: () => apiPost<{ summary: string; status: string; risks: string[]; nextSteps: string[] }>('/ai/record', { entityType, entityId }),
    onError: failed,
  });
  if (!status?.enabled) return null;
  return (
    <Card className="border-primary/20 bg-primary/[0.03]">
      <CardHeader className="flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="flex items-center gap-2 text-base">
          <Sparkles className="h-4 w-4 text-primary" /> AI briefing
        </CardTitle>
        <Button size="sm" variant="outline" onClick={() => brief.mutate()} disabled={brief.isPending}>
          {brief.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
          {brief.data ? 'Refresh' : 'Brief me'}
        </Button>
      </CardHeader>
      {brief.data && (
        <CardContent className="space-y-2 text-sm">
          {brief.data.status && <p className="font-semibold">{brief.data.status}</p>}
          <p>{brief.data.summary}</p>
          {brief.data.risks.length > 0 && (
            <div>
              <p className="text-xs font-semibold text-amber-700">Watch out</p>
              <ul className="list-disc ps-5 text-muted-foreground">
                {brief.data.risks.map((risk) => (
                  <li key={risk}>{risk}</li>
                ))}
              </ul>
            </div>
          )}
          {brief.data.nextSteps.length > 0 && (
            <div>
              <p className="text-xs font-semibold text-primary">Next steps</p>
              <ol className="space-y-1">
                {brief.data.nextSteps.map((step, index) => (
                  <li key={step} className="flex items-start gap-2">
                    <span className="mt-0.5 text-xs font-bold text-muted-foreground">{index + 1}.</span>
                    <span className="flex-1">{step}</span>
                    <TaskFromStep step={step} entityType={entityType} entityId={entityId} />
                  </li>
                ))}
              </ol>
            </div>
          )}
        </CardContent>
      )}
    </Card>
  );
}

/** "Write it for me" in the email composer. */
export function AiEmailWriter({
  entityType,
  entityId,
  onDraft,
}: {
  entityType?: string;
  entityId?: string;
  onDraft: (draft: { subject: string; body: string }) => void;
}) {
  const { data: status } = useAiStatus();
  const [open, setOpen] = useState(false);
  const [instruction, setInstruction] = useState('');
  const [language, setLanguage] = useState<'en' | 'ar'>('en');
  const [tone, setTone] = useState<'professional' | 'friendly' | 'brief' | 'formal'>('professional');
  const draft = useMutation({
    mutationFn: () => apiPost<{ subject: string; body: string }>('/ai/email', { entityType, entityId, instruction, language, tone }),
    onSuccess: (result) => {
      onDraft(result);
      setOpen(false);
    },
    onError: failed,
  });
  if (!status?.enabled) return null;
  if (!open) {
    return (
      <Button type="button" size="sm" variant="outline" onClick={() => setOpen(true)}>
        <Sparkles className="h-4 w-4 text-primary" /> Write with AI
      </Button>
    );
  }
  return (
    <div className="space-y-2 rounded-xl border border-primary/30 bg-primary/[0.03] p-3">
      <textarea
        autoFocus
        rows={2}
        value={instruction}
        onChange={(event) => setInstruction(event.target.value)}
        placeholder="e.g. Follow up on the villa viewing last Tuesday and offer two times this week"
        className="w-full rounded-lg border bg-background px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary/40"
      />
      <div className="flex flex-wrap items-center gap-2">
        <select className="h-8 rounded-lg border bg-background px-2 text-xs" value={tone} onChange={(event) => setTone(event.target.value as typeof tone)}>
          <option value="professional">Professional</option>
          <option value="friendly">Friendly</option>
          <option value="brief">Brief</option>
          <option value="formal">Formal</option>
        </select>
        <select className="h-8 rounded-lg border bg-background px-2 text-xs" value={language} onChange={(event) => setLanguage(event.target.value as 'en' | 'ar')}>
          <option value="en">English</option>
          <option value="ar">العربية</option>
        </select>
        <Button type="button" size="sm" onClick={() => draft.mutate()} disabled={draft.isPending || instruction.trim().length < 3}>
          {draft.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} Write it
        </Button>
        <Button type="button" size="sm" variant="ghost" onClick={() => setOpen(false)}>
          Cancel
        </Button>
      </div>
    </div>
  );
}

/** "Create task" next to an AI next step (Kommo AI task suggestions). */
function TaskFromStep({ step, entityType, entityId }: { step: string; entityType: string; entityId: string }) {
  const [done, setDone] = useState(false);
  const create = useMutation({
    mutationFn: () => apiPost('/tasks', { title: step.slice(0, 200), entityType, entityId, dueAt: new Date(Date.now() + 86_400_000).toISOString() }),
    onSuccess: () => {
      setDone(true);
      toast.success('Task created for tomorrow');
    },
    onError: failed,
  });
  return (
    <button type="button" onClick={() => create.mutate()} disabled={done || create.isPending} className="shrink-0 rounded border px-1.5 py-0.5 text-[11px] font-semibold text-muted-foreground hover:border-primary hover:text-primary disabled:opacity-60">
      {done ? 'Task added' : '+ Task'}
    </button>
  );
}
